/**
 * AnalystDetailPage - Analyst profile with covered companies and recent documents.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  OpenInNew as OpenIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useLanguage } from '../contexts/LanguageContext';
import { getAnalystProfile, AnalystProfile, getCachedAnalystProfile } from '../services/v3RagService';

const AnalystDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<AnalystProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    // Use prefetch cache for instant render if available
    const cached = getCachedAnalystProfile(id);
    if (cached) {
      setProfile(cached);
      setLoading(false);
    } else {
      setLoading(true);
    }
    setError(null);
    getAnalystProfile(id)
      .then(data => setProfile(data))
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !profile) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Analyst not found'}</Alert>
      </Box>
    );
  }

  const { analyst, companies, documents } = profile;

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} size="small">
          <BackIcon />
        </IconButton>
        <PersonIcon sx={{ fontSize: 36, color: 'action.active' }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          {analyst.name}
        </Typography>
        {analyst.sector_focus && (
          <Chip label={analyst.sector_focus} variant="outlined" size="small" />
        )}
        {analyst.ii_ranking && (
          <Chip label={`II #${analyst.ii_ranking}`} color="primary" size="small" />
        )}
      </Box>

      <Grid container spacing={3}>
        {/* Profile Card */}
        <Grid item xs={12} md={4}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Profile</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {analyst.firm && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Firm</Typography>
                    <Typography variant="body1">{analyst.firm}</Typography>
                  </Box>
                )}
                {analyst.sector_focus && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">{t('table.sector')}</Typography>
                    <Typography variant="body1">{analyst.sector_focus}</Typography>
                  </Box>
                )}
                {analyst.years_at_firm != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Years at Firm</Typography>
                    <Typography variant="body1">{analyst.years_at_firm}</Typography>
                  </Box>
                )}
                {analyst.document_count != null && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Documents</Typography>
                    <Typography variant="body1">{analyst.document_count}</Typography>
                  </Box>
                )}
                {analyst.last_document_date && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Last Report</Typography>
                    <Typography variant="body1">{analyst.last_document_date}</Typography>
                  </Box>
                )}
              </Box>

              {analyst.bio && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>Bio</Typography>
                  <Typography variant="body2" color="text.secondary">{analyst.bio}</Typography>
                </Box>
              )}

              {analyst.expertise && analyst.expertise.length > 0 && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom>Expertise</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {analyst.expertise.map((e, i) => (
                      <Chip key={i} label={e} size="small" variant="outlined" />
                    ))}
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Covered Companies */}
        <Grid item xs={12} md={8}>
          <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>Covered Companies ({companies.length})</Typography>
            {companies.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.ticker')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.company')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.sector')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Subsector</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>GICS</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {companies.map(c => (
                      <TableRow
                        key={c.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/company/${c.ticker}`)}
                      >
                        <TableCell>
                          <Chip label={c.ticker} size="small" color="primary" variant="outlined" />
                        </TableCell>
                        <TableCell>{c.name || c.ticker}</TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {c.sector || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {c.sub_sector || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" noWrap>
                            {c.gics_sector || '-'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary">No covered companies</Typography>
            )}
          </Paper>

          {/* Recent Documents */}
          <Paper elevation={2} sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>{t('company.recentDocuments')} ({documents.length})</Typography>
            {documents.length > 0 ? (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.title')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.date')}</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>{t('table.sector')}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map(doc => (
                      <TableRow
                        key={doc.id}
                        hover
                        sx={{ cursor: 'pointer' }}
                        onClick={() => navigate(`/document-pro?id=${doc.id}`)}
                      >
                        <TableCell sx={{ maxWidth: 400 }}>
                          <Typography variant="body2" noWrap title={doc.title || ''}>
                            {doc.title || 'Untitled'}
                          </Typography>
                          {doc.company_tickers && doc.company_tickers.length > 0 && (
                            <Box sx={{ mt: 0.5, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                              {doc.company_tickers.map(t => (
                                <Chip key={t} label={t} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.7rem' }} />
                              ))}
                            </Box>
                          )}
                        </TableCell>
                        <TableCell>{doc.report_date || '-'}</TableCell>
                        <TableCell>{doc.sector || '-'}</TableCell>
                        <TableCell>
                          <OpenIcon fontSize="small" color="action" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                No recent documents
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalystDetailPage;
